import { supabase } from '../supabaseClient';
import { resolveForfeoReserveEdgeErrorCode } from './forfeoReserveEdgeError';
import {
  validateForfeoReservePolicyCommand,
  validateForfeoReservePolicyResolutionRequest,
  type ForfeoReservePolicyCommand,
  type ForfeoReservePolicyResolutionRequest,
  type ForfeoReservePolicySnapshot,
  type ForfeoReserveResolvedCommercialPolicy,
} from '../../supabase/functions/_shared/forfeo-reserve-policy';

export const FORFEO_RESERVE_POLICY_FEATURE_FLAG =
  'VITE_FORFEO_RESERVE_POLICIES_V1' as const;

export function isForfeoReservePolicyEnabled(): boolean {
  return import.meta.env.VITE_FORFEO_RESERVE_POLICIES_V1 === 'true';
}

export class ForfeoReservePolicyClientError extends Error {
  readonly code: string;

  constructor(code: string) {
    super(code);
    this.name = 'ForfeoReservePolicyClientError';
    this.code = code;
  }
}

function correlationId(): string {
  return `reserve-policy:${crypto.randomUUID()}`;
}

function idempotencyKey(kind: string): string {
  return `reserve:policy:${kind}:${crypto.randomUUID()}`;
}

async function invokePolicy<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke(
    'reserve-commercial-policies',
    { body },
  );
  if (error) {
    throw new ForfeoReservePolicyClientError(
      await resolveForfeoReserveEdgeErrorCode(
        data,
        error,
        'policy_network_error',
      ),
    );
  }
  if (!data?.ok) {
    throw new ForfeoReservePolicyClientError(
      String(data?.error || 'policy_operation_failed'),
    );
  }
  return data as T;
}

export function createForfeoReservePolicyCommand(
  input: Omit<ForfeoReservePolicyCommand, 'correlationId' | 'idempotencyKey'>
    & Partial<Pick<ForfeoReservePolicyCommand, 'correlationId' | 'idempotencyKey'>>,
): ForfeoReservePolicyCommand {
  const command: ForfeoReservePolicyCommand = {
    ...input,
    correlationId: input.correlationId || correlationId(),
    idempotencyKey: input.idempotencyKey || idempotencyKey(input.kind),
  };
  const errors = validateForfeoReservePolicyCommand(command);
  if (errors.length > 0) {
    throw new ForfeoReservePolicyClientError(errors.join(','));
  }
  return command;
}

export function createForfeoReservePolicyResolution(
  input: Omit<ForfeoReservePolicyResolutionRequest, 'correlationId'>
    & Partial<Pick<ForfeoReservePolicyResolutionRequest, 'correlationId'>>,
): ForfeoReservePolicyResolutionRequest {
  const resolution: ForfeoReservePolicyResolutionRequest = {
    ...input,
    correlationId: input.correlationId || correlationId(),
  };
  const errors = validateForfeoReservePolicyResolutionRequest(resolution);
  if (errors.length > 0) {
    throw new ForfeoReservePolicyClientError(errors.join(','));
  }
  return resolution;
}

export async function fetchForfeoReservePolicySnapshot(
  businessId: string,
): Promise<ForfeoReservePolicySnapshot> {
  const data = await invokePolicy<{
    ok: true;
    snapshot: ForfeoReservePolicySnapshot;
  }>({ action: 'snapshot', businessId });
  return data.snapshot;
}

export async function resolveForfeoReserveCommercialPolicy(
  resolution: ForfeoReservePolicyResolutionRequest,
): Promise<ForfeoReserveResolvedCommercialPolicy> {
  const errors = validateForfeoReservePolicyResolutionRequest(resolution);
  if (errors.length > 0) {
    throw new ForfeoReservePolicyClientError(errors.join(','));
  }
  const data = await invokePolicy<{
    ok: true;
    resolution: ForfeoReserveResolvedCommercialPolicy;
  }>({ action: 'resolve', resolution });
  return data.resolution;
}

export async function applyForfeoReservePolicyCommand(
  command: ForfeoReservePolicyCommand,
): Promise<Record<string, unknown>> {
  const errors = validateForfeoReservePolicyCommand(command);
  if (errors.length > 0) {
    throw new ForfeoReservePolicyClientError(errors.join(','));
  }
  const data = await invokePolicy<{
    ok: true;
    result: Record<string, unknown>;
  }>({ action: 'command', command });
  return data.result;
}
